import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  PlatformClinic,
  PlatformClinicsAPI,
  DEFAULT_CREDIT_GRANT_PAISE,
} from "../../services/platformClinics.service";

interface Props {
  isOpen: boolean;
  clinic: PlatformClinic | null;
  onClose: () => void;
  onSuccess: () => void;
}

/** Rs -> paise. Money is integer paise everywhere; the operator types rupees. */
function rupeesToPaise(rupees: string): number {
  return Math.round(Number(rupees) * 100);
}

function formatRupees(paise: number): string {
  return (paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

/**
 * Discretionary credit top-up for one clinic.
 *
 * A clinic's included balance is a single Rs 100 allowance per billing period,
 * spent from one wallet across comms sends (Rs 0.50 each) and AI Scribe runs
 * (Rs 3 each). This is the operator's lever when that runs out and there is a
 * reason not to sell a top-up.
 *
 * The grant is not idempotent server-side, so the submit button is the guard
 * against a double click.
 */
const GrantCreditsModal: React.FC<Props> = ({ isOpen, clinic, onClose, onSuccess }) => {
  const [rupees, setRupees] = useState(String(DEFAULT_CREDIT_GRANT_PAISE / 100));
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (clinic) {
      setRupees(String(DEFAULT_CREDIT_GRANT_PAISE / 100));
      setReason("");
    }
  }, [clinic]);

  if (!isOpen || !clinic) return null;

  const amountPaise = rupeesToPaise(rupees);
  const amountValid = Number.isFinite(amountPaise) && amountPaise > 0;
  const reasonValid = reason.trim().length >= 3;

  const handleGrant = async () => {
    if (!amountValid) {
      toast.error("Enter an amount greater than zero");
      return;
    }
    if (!reasonValid) {
      toast.error("A reason is required — it is written to the audit log");
      return;
    }
    setLoading(true);
    try {
      const res = await PlatformClinicsAPI.grantCredits(
        clinic.clinic_id,
        amountPaise,
        reason.trim()
      );
      toast.success(
        `Added Rs ${formatRupees(res.amount_paise)} to ${clinic.name} — ` +
          `balance now Rs ${formatRupees(res.balance_paise)}`
      );
      onSuccess();
      onClose();
    } catch (e: any) {
      toast.error(e?.message || "Failed to add credits");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-teal-600">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v8M8 12h8" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-800 leading-tight">Add credits</h2>
              <p className="text-xs text-slate-500 mt-0.5">{clinic.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            className="text-slate-400 hover:text-slate-600 text-xl leading-none font-bold disabled:opacity-40"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="px-6 pb-5 space-y-4">
          <p className="text-sm text-slate-600 leading-relaxed">
            One wallet funds both WhatsApp sends (Rs 0.50 each) and AI Scribe runs (Rs 3 each).
            The plan includes Rs 100 per billing period; anything beyond that is this grant.
          </p>

          {/* Amount */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Amount in rupees <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={rupees}
              onChange={e => setRupees(e.target.value)}
              autoFocus
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0066ff]"
            />
            {amountValid && (
              <p className="text-xs text-slate-400 mt-1">
                {amountPaise} paise · about {Math.floor(amountPaise / 50)} sends or{" "}
                {Math.floor(amountPaise / 300)} scribe runs
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. pilot clinic, goodwill after outage"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0066ff]"
            />
            <p className="text-xs text-slate-400 mt-1">
              Written to the audit log — this is unpurchased balance, so it has to be explainable.
            </p>
          </div>

          {/* Endpoint that will be called — read-only chip */}
          <div className="rounded-lg bg-slate-100 border border-slate-200 px-3 py-2.5 text-sm text-slate-500 font-mono select-all">
            POST /platform/clinics/{clinic.clinic_id}/credits
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm text-slate-600 rounded-xl hover:bg-slate-50 disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleGrant}
              disabled={loading || !amountValid || !reasonValid}
              className="px-4 py-2 bg-[#2563eb] text-white text-sm font-medium rounded-xl hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Adding…" : "Add credits"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrantCreditsModal;
